/**
 * workshopCreateVenues — turns the venues API's region-keyed groups into the
 * Location step's venue options.
 *
 * Three rules, each fixing something the plain key lookup this replaces got
 * wrong (BON-453):
 *
 * Membership rolls up. A venue belongs to the region it is attached to AND to
 * every region above it, so selecting a parent must offer the venues of its
 * subregions too. Matching the selected region's id alone hid all of them.
 *
 * No region selected means the viewer's own regions, not every region. The old
 * lookup flattened the entire map, so a viewer entitled to nothing was offered
 * every venue in the database — the defect actually reported. Scoping to the
 * writable tree keeps the venue-first flow (pick a venue, adopt its region)
 * while making an empty writable tree offer nothing at all.
 *
 * The chosen venue always survives. A venue already selected stays in the list
 * even when the scope above would exclude it, or the select would quietly drop
 * its own value.
 */
import type { RegionNode } from '../../../../api/regions';
import { flattenById, subtreeIds } from '../../../../utils/regionTree';

import type { VenueOption } from './workshopCreateSteps';

export type VenueGroups = Record<string, { id: string; name: string }[]>;

/** The group key (region id, or the `unassigned` pseudo-bucket) a venue sits
 *  under, or undefined when the venues data does not know it. */
function groupOf(groups: VenueGroups, venueId: string): string | undefined {
  return Object.keys(groups).find((key) => groups[key].some((v) => v.id === venueId));
}

/**
 * The venues on offer for `regionId`, or across everything the viewer may write
 * to when no region is chosen yet.
 *
 * The selected region is included even when it does not resolve in `regions`:
 * the venue-detail entry point fixes a region taken from the venue itself,
 * which need not appear in the viewer's writable tree, and dropping it would
 * leave that flow unable to name its own venue.
 */
export function toVenueOptions(
  grouped: VenueGroups | undefined,
  regionId: string | null,
  regions: RegionNode[],
  selectedVenueId = '',
): VenueOption[] {
  const map = grouped ?? {};
  const byId = flattenById(regions);
  const owners = regionId ? subtreeIds(byId, regionId) : new Set(byId.keys());
  if (regionId) owners.add(regionId);
  if (selectedVenueId) {
    const owner = groupOf(map, selectedVenueId);
    if (owner) owners.add(owner);
  }
  return [...owners].flatMap((owner) =>
    (map[owner] ?? []).map((v) => ({ id: v.id, name: v.name, regionId: owner })),
  );
}
